'use client'

// GA 측정 ID는 공개 식별자다. Amplify 환경변수가 누락돼도 출시 URL에서 계측되도록
// 전용 속성 ID를 안전한 기본값으로 유지한다.
export const TAEKWONDO_GA_ID =
  process.env.NEXT_PUBLIC_TAEKWONDO_GA_ID || 'G-7B8SQ3G4VG'

export type TaekwondoAnalyticsEvent =
  | 'avatar_experience_view'
  | 'avatar_session_start'
  | 'question_submit'
  | 'avatar_response'
  | 'first_avatar_response'
  | 'chat_mode_change'
  | 'language_change'
  | 'media_toggle'
  | 'avatar_session_end'
  | 'service_error'

export type TaekwondoEventParams = Record<
  string,
  string | number | boolean | null | undefined
>

type PendingEvent = {
  eventName: TaekwondoAnalyticsEvent
  params: TaekwondoEventParams
}

let isAnalyticsReady = false
let pendingEvents: PendingEvent[] = []
let readinessRetryId: number | null = null
let readinessRetryCount = 0

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
  }
}

/**
 * 태권낭자 전용 GA4 이벤트를 별도 속성으로 전송합니다.
 *
 * 중요: 질문/답변 본문, 음성, 사용자 식별자 등 개인정보가 될 수 있는 값은
 * 파라미터로 전달하지 않습니다. 길이·모드·언어·지연시간 등 집계값만 보냅니다.
 */
export function sendTaekwondoEvent(
  eventName: TaekwondoAnalyticsEvent,
  params: TaekwondoEventParams = {},
): boolean {
  if (!TAEKWONDO_GA_ID || typeof window === 'undefined') {
    return false
  }

  if (!isAnalyticsReady || typeof window.gtag !== 'function') {
    pendingEvents.push({ eventName, params })
    return true
  }

  window.gtag('event', eventName, {
    ...params,
    send_to: TAEKWONDO_GA_ID,
  })

  return true
}

export function setTaekwondoAnalyticsReady(ready: boolean): void {
  isAnalyticsReady = ready
  if (!ready) {
    if (typeof window !== 'undefined' && readinessRetryId !== null) {
      window.clearTimeout(readinessRetryId)
    }
    readinessRetryId = null
    readinessRetryCount = 0
    pendingEvents = []
    return
  }
  if (typeof window === 'undefined') return
  if (typeof window.gtag !== 'function') {
    // 느린 모바일 네트워크에서 Google 태그 로드가 늦어져도 최대 10초간 재확인합니다.
    if (readinessRetryCount < 40) {
      readinessRetryCount += 1
      readinessRetryId = window.setTimeout(() => setTaekwondoAnalyticsReady(true), 250)
    }
    return
  }

  if (readinessRetryId !== null) window.clearTimeout(readinessRetryId)
  readinessRetryId = null
  readinessRetryCount = 0

  const events = pendingEvents
  pendingEvents = []
  for (const { eventName, params } of events) {
    window.gtag('event', eventName, {
      ...params,
      send_to: TAEKWONDO_GA_ID,
    })
  }
}

export function getTaekwondoSurface(pathname: string): 'slug' | 'expo2' | null {
  if (pathname === '/taekwondo' || pathname.startsWith('/taekwondo/')) return 'slug'
  if (pathname.startsWith('/expo2/')) return 'expo2'
  return null
}

export function getResponseTimeBucket(responseTimeMs: number): string {
  if (responseTimeMs < 1000) return 'under_1s'
  if (responseTimeMs < 3000) return '1_3s'
  if (responseTimeMs < 5000) return '3_5s'
  if (responseTimeMs < 10000) return '5_10s'
  return 'over_10s'
}
